/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/src/data-import/client.tsx`.
 *
 * Changes, and only these:
 *   `"use client"`                        -> dropped (inert in this host)
 *   `useRouter` from next/navigation      -> `useNavigate` from @excellent/ui/router
 *   `router.refresh()`                    -> `navigate.refresh()`
 *
 * `refresh()` is not a one-to-one mapping and that is deliberate, decided once in
 * `client/shell/router-adapter.tsx`: Next's `router.refresh()` re-ran the server
 * render; here the adapter maps it to `router.invalidate()` (re-run the loaders).
 * The call site is unchanged because the seam absorbs the difference — which is
 * the whole point of the seam.
 *
 * ⚠️ RUNTIME-BLOCKED, code-complete: `dataImport.preview` and `dataImport.run`
 * are NOT yet mounted on the Hono host (checked against every `mount(app, …)` in
 * `excellent/server/`). The component compiles, bundles and renders; the two
 * callbacks will 404 until L1 ports the `/api/data-import/*` namespace. Listed in
 * `docs/rollover/inventory/shared-render.md` §6 with the other 8 contracts in the
 * same state — do not "fix" it with a `fetch()`.
 */

import { useNavigate } from "@excellent/ui/router";
import { apiCall } from "@excellent/ui/api";
import { dataImport } from "@excellent/ui/api/contracts/data-import";
import { TypedImportWizard } from "@excellent/ui/blocks/data-import/typed-import-wizard";
import { getImportTarget } from "@excellent/ui/blocks/data-import/targets";

export function DataImportClient({ targetKind }: { targetKind: string }) {
  const navigate = useNavigate();
  const target = getImportTarget(targetKind);
  if (!target) {
    return (
      <div className="type-body text-destructive">
        Unknown import target: <code>{targetKind}</code>
      </div>
    );
  }
  return (
    <TypedImportWizard
      target={target}
      onPreview={({ filename, file_base64 }) =>
        apiCall(dataImport.preview, { body: { filename, file_base64 } })
      }
      onRun={async ({ filename, file_base64, mapping }) => {
        const r = await apiCall(dataImport.run, {
          body: { target: targetKind, filename, file_base64, mapping },
        });
        navigate.refresh();
        return { imported_rows: r.imported_rows, failed_rows: r.failed_rows };
      }}
    />
  );
}
